import { Select, Typography } from "antd";
import type { RefSelectProps } from "antd";
import type { CustomCellEditorProps } from "ag-grid-react";
import debounce from "lodash.debounce";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useCIQLazyQuery } from "hooks/ciq-gql-hooks";
import type { CsiSystem, CsiTerm } from "design-scope";
import { ClassBadge } from "pages/design-scope-page/components/scope-ui";
import { SEARCH_CLASSIFICATION_ITEMS } from "pages/design-scope-page/queries";
import {
  buildClassificationSearchVariables,
  mapGqlClassificationItem,
  type GqlClassificationItem
} from "pages/design-scope-page/shared/classification-catalog";
import type { HTTPClientResponse } from "services/ciq-client";
import { getAccessToken } from "services/auth";

export type ClassificationSearchSelection = {
  id: string | number;
  name: string;
  code: string;
  system: "UNIFORMAT" | "MASTERFORMAT";
};

const SYSTEM_LABEL: Record<CsiSystem, string> = {
  UF: "UniFormat",
  MF: "MasterFormat"
};

const STORED_SYSTEM: Record<
  CsiSystem,
  ClassificationSearchSelection["system"]
> = {
  UF: "UNIFORMAT",
  MF: "MASTERFORMAT"
};

const SEARCH_DEBOUNCE_MS = 300;

type ClassificationOption = {
  value: string | number;
  label: ReactNode;
  selection: ClassificationSearchSelection;
};

function rowsFromResponse(response: HTTPClientResponse): CsiTerm[] {
  if (!response.success || response.data?.errors?.length) return [];
  const rows = (response.data?.data?.classification_items ??
    []) as GqlClassificationItem[];
  return rows
    .map(mapGqlClassificationItem)
    .filter((term): term is CsiTerm => term !== null);
}

function toSelection(term: CsiTerm): ClassificationSearchSelection {
  return {
    id: /^\d+$/.test(term.id) ? Number(term.id) : term.id,
    name: term.title,
    code: term.number,
    system: STORED_SYSTEM[term.system]
  };
}

function optionLabel(system: CsiSystem, code: string, title: string) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <ClassBadge system={system} code={code} />
      <span className="min-w-0 truncate">{title}</span>
    </span>
  );
}

type ClassificationSearchSelectProps = {
  system: CsiSystem;
  value: ClassificationSearchSelection | null;
  onChange: (selection: ClassificationSearchSelection | null) => void;
  openOnMount?: boolean;
  fillHeight?: boolean;
  testId?: string;
};

/**
 * Live classification search. The result list is the select dropdown.
 * The subscription access token is used when a project token is not present.
 */
export function ClassificationSearchSelect({
  system,
  value,
  onChange,
  openOnMount = false,
  fillHeight = false,
  testId
}: ClassificationSearchSelectProps) {
  const current = value;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CsiTerm[]>([]);
  const [open, setOpen] = useState(openOnMount);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchGen = useRef(0);
  const selectRef = useRef<RefSelectProps>(null);
  const [searchItems] = useCIQLazyQuery(SEARCH_CLASSIFICATION_ITEMS, {
    token: getAccessToken() ?? undefined
  });

  useEffect(() => {
    if (!openOnMount) return;
    selectRef.current?.focus();
  }, [openOnMount]);

  const runSearch = useMemo(
    () =>
      debounce(async (nextQuery: string) => {
        const generation = ++searchGen.current;
        const variables = buildClassificationSearchVariables(nextQuery, system);
        if (!variables) {
          setResults([]);
          setLoading(false);
          setError(null);
          return;
        }
        setLoading(true);
        setError(null);
        try {
          const response = await searchItems(variables);
          if (generation !== searchGen.current) return;
          if (!response.success || response.data?.errors?.length) {
            setResults([]);
            setError("Could not search classifications.");
            return;
          }
          setResults(rowsFromResponse(response));
        } catch {
          if (generation !== searchGen.current) return;
          setResults([]);
          setError("Could not search classifications.");
        } finally {
          if (generation === searchGen.current) setLoading(false);
        }
      }, SEARCH_DEBOUNCE_MS),
    [searchItems, system]
  );

  useEffect(
    () => () => {
      runSearch.cancel();
      searchGen.current += 1;
    },
    [runSearch]
  );

  const search = (nextQuery: string) => {
    setQuery(nextQuery);
    if (!nextQuery.trim()) {
      runSearch.cancel();
      searchGen.current += 1;
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    runSearch(nextQuery);
  };

  const options = useMemo(() => {
    const fromResults: ClassificationOption[] = results.map((term) => ({
      value: toSelection(term).id,
      label: optionLabel(term.system, term.number, term.title),
      selection: toSelection(term)
    }));
    if (current?.id == null) return fromResults;
    const alreadyListed = fromResults.some(
      (option) => String(option.value) === String(current.id)
    );
    if (alreadyListed) return fromResults;
    return [
      {
        value: current.id,
        label: optionLabel(system, current.code, current.name),
        selection: current
      },
      ...fromResults
    ];
  }, [current, results, system]);

  const pick = (selection: ClassificationSearchSelection) => {
    runSearch.cancel();
    searchGen.current += 1;
    onChange(selection);
  };

  let notFound: ReactNode = null;
  if (!query.trim()) {
    notFound = `Type to search ${SYSTEM_LABEL[system]}`;
  } else if (loading) {
    notFound = "Searching…";
  } else if (error) {
    notFound = <Typography.Text type="danger">{error}</Typography.Text>;
  } else {
    notFound = `No matching ${SYSTEM_LABEL[system]} code.`;
  }

  return (
    <div className={fillHeight ? "flex !h-full w-full items-center" : "w-full"}>
      <Select
        ref={selectRef}
        className="w-full"
        showSearch
        allowClear
        filterOption={false}
        open={open}
        value={current?.id}
        options={options}
        loading={loading}
        placeholder={`Search ${SYSTEM_LABEL[system]}`}
        notFoundContent={notFound}
        data-testid={testId ?? `classification-search-editor-${system}`}
        onOpenChange={setOpen}
        onSearch={search}
        onChange={(selected, option) => {
          if (selected == null) {
            runSearch.cancel();
            searchGen.current += 1;
            onChange(null);
            return;
          }
          const picked = option as ClassificationOption | undefined;
          if (picked?.selection) pick(picked.selection);
        }}
      />
    </div>
  );
}

type ClassificationSearchCellEditorProps = CustomCellEditorProps<
  unknown,
  ClassificationSearchSelection | null
> & {
  system: CsiSystem;
};

/** In-cell wrapper. Choosing a result commits the value and closes the editor. */
export default function ClassificationSearchCellEditor({
  system,
  value,
  onValueChange,
  stopEditing
}: ClassificationSearchCellEditorProps) {
  const current = (value ?? null) as ClassificationSearchSelection | null;
  return (
    <ClassificationSearchSelect
      system={system}
      value={current}
      openOnMount
      fillHeight
      testId={`classification-search-editor-${system}`}
      onChange={(selection) => {
        onValueChange(selection);
        setTimeout(() => {
          stopEditing();
        }, 200);
      }}
    />
  );
}
